import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { calculateSourcerCommission } from "./DealSourcerDashboard";
import { DollarSign, CheckCircle2, Clock } from "lucide-react";

const fmtUSD = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function DealSourcerCommissions() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const lang = (language as "en" | "fr") || "en";
  const [deals, setDeals] = useState<any[]>([]);

  useEffect(() => {
    if (!user?.id) return;
    const q = query(collection(db, "dealSubmissions"), where("sourcedBy", "==", user.id), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => setDeals(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user?.id]);

  const stats = useMemo(() => {
    const closed = deals.filter(d => (d.currentStage || d.stage) === "closing");
    const pending = deals.filter(d => (d.currentStage || d.stage) !== "closing");
    return {
      paid: closed.reduce((acc, d) => acc + calculateSourcerCommission(d.sourcerCommission || d.askingPrice || 0), 0),
      potential: pending.reduce((acc, d) => acc + calculateSourcerCommission(d.askingPrice || 0), 0),
      total: deals.reduce((acc, d) => acc + calculateSourcerCommission(d.askingPrice || 0), 0),
    };
  }, [deals]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-[#1D314D]">
        {lang === "fr" ? "Mes commissions" : "My Commissions"}
      </h1>
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{lang === "fr" ? "Commissions versées" : "Paid commissions"}</CardDescription>
            <CardTitle className="text-2xl text-green-600 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />{fmtUSD(stats.paid)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{lang === "fr" ? "Commissions potentielles" : "Potential commissions"}</CardDescription>
            <CardTitle className="text-2xl text-[#CEAB65] flex items-center gap-2">
              <Clock className="h-5 w-5" />{fmtUSD(stats.potential)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{lang === "fr" ? "Total estimé" : "Total estimated"}</CardDescription>
            <CardTitle className="text-2xl text-[#1D314D] flex items-center gap-2">
              <DollarSign className="h-5 w-5" />{fmtUSD(stats.total)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-[#1D314D]">{lang === "fr" ? "Barème" : "Commission Schedule"}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-5 text-sm">
            {[
              { range: "$250K–$500K", payout: "$250" },
              { range: "$500K–$1M", payout: "$500" },
              { range: "$1M–$2.5M", payout: "$1,000" },
              { range: "$2.5M–$5M", payout: "$2,000" },
              { range: "$5M+", payout: "$3,500" },
            ].map(t => (
              <div key={t.range} className="rounded-lg border p-3 text-center">
                <div className="text-xs text-muted-foreground">{t.range}</div>
                <div className="text-lg font-semibold text-[#CEAB65]">{t.payout}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}